// Imports (spec § 5) : fichier mensuel de l'ISVZ (marchés publics tchèques), exports trimestriels par pays de Kohesio
// (politique de cohésion de l'UE), lecture trimestrielle des études d'impact japonaises par le navigateur et fichier semestriel des villes de GeoNames (carte des projets), téléchargés à la main par le responsable des imports et déposés ici. Réservé à ce responsable par la base (`is_imports_owner`), pas par le rôle
// d'administrateur : les comptes des clients sont administrateurs.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, ExternalLink, FileUp } from "lucide-react";
import { useLocaleState, useTranslate } from "ra-core";
import { useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { getSupabaseClient } from "../providers/supabase/supabase";
import {
  GEONAMES_DUMP,
  geonamesFileOk,
  readGeonamesZip,
  semesterPeriod,
} from "./geonamesImport";
import {
  ISVZ_OPEN_DATA,
  type CompactRecord,
  periodFromFileName,
  readIsvzZip,
} from "./isvzImport";
import { MailGroupProposals } from "./MailGroupProposals";
import { JPEIA_SITE, readJpeia } from "./jpeiaImport";
import { KOHESIO_DATA, quarterPeriod, readKohesioFiles } from "./kohesioImport";
import { useIsImportsOwner } from "./useIsImportsOwner";

// Les villes sont petites et nombreuses (environ 140 000) : envoyées par lots plus gros que les marchés.
const BATCH: Record<ImportSource, number> = {
  isvz: 200,
  geonames: 1000,
  kohesio: 500,
  jpeia: 100,
};

type ImportSource = "isvz" | "geonames" | "kohesio" | "jpeia";

type SourceImport = {
  id: number;
  // Le MIRT, le Verkehrsinvestitionsbericht, le ÖBB-Rahmenplan et le Krajowy Program Kolejowy annuels sont
  // enregistrés par le worker (ligne de commande), jamais déposés depuis cet écran.
  source_id: ImportSource | "mirt" | "vib" | "rahmenplan" | "kpk" | "ntp" | "budjetti" | "standbericht" | "sfdi";
  period: string;
  file_name: string;
  record_count: number;
  status: "uploading" | "pending" | "running" | "done" | "error";
  result: {
    records?: number;
    stale?: number;
    kept?: number;
    places?: number;
    placed?: number;
    projects?: number;
  } | null;
  error: string | null;
  created_at: string;
  finished_at: string | null;
};

type Step =
  | { kind: "idle" }
  | { kind: "reading"; ratio: number; contracts: number; kept: number }
  | { kind: "reading_cities"; ratio: number; cities: number }
  | { kind: "reading_kohesio"; ratio: number; operations: number; kept: number }
  | { kind: "sending_kohesio"; sent: number; total: number }
  | { kind: "reading_jpeia"; read: number; kept: number; details?: number }
  | { kind: "sending_jpeia"; sent: number; total: number }
  | { kind: "sending"; sent: number; total: number }
  | { kind: "sending_cities"; sent: number; total: number }
  | {
      kind: "sent";
      source: ImportSource;
      total: number;
      period: string;
      batch?: string;
    }
  | { kind: "error"; message: string };

const send = async (
  source: ImportSource,
  fileName: string,
  period: string,
  records: unknown[],
  onSent: (sent: number) => void,
) => {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("source_imports")
    .insert({
      source_id: source,
      period,
      file_name: fileName,
      record_count: records.length,
    })
    .select("id")
    .single();
  if (error) throw error;
  for (let start = 0; start < records.length; start += BATCH[source]) {
    const rows = records
      .slice(start, start + BATCH[source])
      .map((record, index) => ({
        import_id: data.id,
        position: start + index,
        record,
      }));
    const { error } = await supabase.from("source_import_records").insert(rows);
    if (error) throw error;
    onSent(start + rows.length);
  }
  const { error: finishError } = await supabase.rpc("finish_source_import", {
    import: data.id,
  });
  if (finishError) throw finishError;
};

export const SourceImportsPage = () => {
  const translate = useTranslate();
  const t = (key: string, options?: object) =>
    translate(`resources.projects.imports.${key}`, options);
  const [locale = "fr"] = useLocaleState();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const citiesInput = useRef<HTMLInputElement>(null);
  const kohesioInput = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>({ kind: "idle" });
  // Carte dont vient l'état affiché (lecture, envoi, erreur).
  const [active, setActive] = useState<ImportSource>("isvz");
  const owner = useIsImportsOwner();
  const history = useQuery({
    queryKey: ["source_imports"],
    enabled: owner.data === true,
    refetchOnMount: "always",
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("source_imports")
        .select(
          "id, source_id, period, file_name, record_count, status, result, error, created_at, finished_at",
        )
        .order("created_at", { ascending: false })
        .limit(36);
      if (error) throw error;
      return data as SourceImport[];
    },
  });

  if (owner.isPending) return null;
  if (!owner.data) {
    return (
      <p className="mt-4 text-sm text-muted-foreground">{t("forbidden")}</p>
    );
  }

  const busy =
    step.kind === "reading" ||
    step.kind === "reading_cities" ||
    step.kind === "sending" ||
    step.kind === "sending_cities" ||
    step.kind === "reading_kohesio" ||
    step.kind === "sending_kohesio" ||
    step.kind === "reading_jpeia" ||
    step.kind === "sending_jpeia";
  const number = (value: number) => value.toLocaleString(locale);

  const choose = async (file: File | undefined) => {
    if (!file) return;
    setActive("isvz");
    const period = periodFromFileName(file.name);
    if (!period) {
      setStep({ kind: "error", message: t("bad_name", { name: file.name }) });
      return;
    }
    try {
      setStep({ kind: "reading", ratio: 0, contracts: 0, kept: 0 });
      const records: CompactRecord[] = await readIsvzZip(file, (progress) =>
        setStep({
          kind: "reading",
          ratio: progress.bytesRead / Math.max(progress.totalBytes, 1),
          contracts: progress.contracts,
          kept: progress.kept,
        }),
      );
      if (!records.length) throw new Error(t("nothing_kept"));
      setStep({ kind: "sending", sent: 0, total: records.length });
      await send("isvz", file.name, period, records, (sent) =>
        setStep({ kind: "sending", sent, total: records.length }),
      );
      setStep({ kind: "sent", source: "isvz", total: records.length, period });
    } catch (error) {
      const message =
        error instanceof Error && error.message === "no_json"
          ? t("no_json")
          : error instanceof Error
            ? error.message
            : String(error);
      setStep({ kind: "error", message });
    } finally {
      if (input.current) input.current.value = "";
      queryClient.invalidateQueries({ queryKey: ["source_imports"] });
    }
  };

  const chooseCities = async (file: File | undefined) => {
    if (!file) return;
    setActive("geonames");
    if (!geonamesFileOk(file.name)) {
      setStep({
        kind: "error",
        message: t("geonames_bad_name", { name: file.name }),
      });
      return;
    }
    const period = semesterPeriod(new Date());
    try {
      setStep({ kind: "reading_cities", ratio: 0, cities: 0 });
      const cities = await readGeonamesZip(file, (progress) =>
        setStep({
          kind: "reading_cities",
          ratio: progress.bytesRead / Math.max(progress.totalBytes, 1),
          cities: progress.cities,
        }),
      );
      if (cities.length < 1000) throw new Error(t("geonames_too_few"));
      setStep({ kind: "sending_cities", sent: 0, total: cities.length });
      await send("geonames", file.name, period, cities, (sent) =>
        setStep({ kind: "sending_cities", sent, total: cities.length }),
      );
      setStep({
        kind: "sent",
        source: "geonames",
        total: cities.length,
        period,
      });
    } catch (error) {
      const message =
        error instanceof Error && error.message === "no_txt"
          ? t("geonames_no_txt")
          : error instanceof Error
            ? error.message
            : String(error);
      setStep({ kind: "error", message });
    } finally {
      if (citiesInput.current) citiesInput.current.value = "";
      queryClient.invalidateQueries({ queryKey: ["source_imports"] });
    }
  };

  const chooseKohesio = async (list: FileList | null) => {
    const files = list ? [...list] : [];
    if (!files.length) return;
    setActive("kohesio");
    const period = quarterPeriod(new Date());
    try {
      setStep({ kind: "reading_kohesio", ratio: 0, operations: 0, kept: 0 });
      const { export: batch, records } = await readKohesioFiles(
        files,
        (progress) =>
          setStep({
            kind: "reading_kohesio",
            ratio: progress.bytesRead / Math.max(progress.totalBytes, 1),
            operations: progress.operations,
            kept: progress.kept,
          }),
      );
      if (!records.length) throw new Error(t("kohesio_nothing"));
      setStep({ kind: "sending_kohesio", sent: 0, total: records.length });
      const fileName = `Kohesio ${batch} (${files.length} pays)`;
      await send("kohesio", fileName, period, records, (sent) =>
        setStep({ kind: "sending_kohesio", sent, total: records.length }),
      );
      setStep({
        kind: "sent",
        source: "kohesio",
        total: records.length,
        period,
        batch,
      });
    } catch (error) {
      const raw = error instanceof Error ? error.message : String(error);
      const message = raw.startsWith("bad_name:")
        ? t("kohesio_bad_name", { name: raw.slice("bad_name:".length) })
        : raw === "mixed_exports"
          ? t("kohesio_mixed")
          : raw;
      setStep({ kind: "error", message });
    } finally {
      if (kohesioInput.current) kohesioInput.current.value = "";
      queryClient.invalidateQueries({ queryKey: ["source_imports"] });
    }
  };

  const readJapan = async () => {
    setActive("jpeia");
    const period = quarterPeriod(new Date());
    try {
      setStep({ kind: "reading_jpeia", read: 0, kept: 0 });
      const { records } = await readJpeia(new Date(), (progress) =>
        setStep({
          kind: "reading_jpeia",
          read: progress.read,
          kept: progress.kept,
          details: progress.phase === "detail" ? progress.details : undefined,
        }),
      );
      if (!records.length) throw new Error(t("jpeia_nothing"));
      setStep({ kind: "sending_jpeia", sent: 0, total: records.length });
      await send(
        "jpeia",
        `Études d'impact ${period}`,
        period,
        records,
        (sent) =>
          setStep({ kind: "sending_jpeia", sent, total: records.length }),
      );
      setStep({ kind: "sent", source: "jpeia", total: records.length, period });
    } catch (error) {
      const raw = error instanceof Error ? error.message : String(error);
      const message =
        raw.startsWith("list:") || error instanceof TypeError
          ? t("jpeia_refused", { error: raw })
          : raw;
      setStep({ kind: "error", message });
    } finally {
      queryClient.invalidateQueries({ queryKey: ["source_imports"] });
    }
  };

  const status = (
    <>
      {step.kind === "reading_cities" && (
        <div className="flex flex-col gap-1">
          <Progress value={Math.round(step.ratio * 100)} />
          <span className="text-sm text-muted-foreground">
            {t("geonames_reading", { cities: number(step.cities) })}
          </span>
        </div>
      )}
      {step.kind === "reading" && (
        <div className="flex flex-col gap-1">
          <Progress value={Math.round(step.ratio * 100)} />
          <span className="text-sm text-muted-foreground">
            {t("reading", {
              contracts: number(step.contracts),
              kept: number(step.kept),
            })}
          </span>
        </div>
      )}
      {step.kind === "sending" && (
        <div className="flex flex-col gap-1">
          <Progress value={Math.round((step.sent / step.total) * 100)} />
          <span className="text-sm text-muted-foreground">
            {t("sending", {
              sent: number(step.sent),
              total: number(step.total),
            })}
          </span>
        </div>
      )}
      {step.kind === "sending_cities" && (
        <div className="flex flex-col gap-1">
          <Progress
            value={Math.round((step.sent / Math.max(step.total, 1)) * 100)}
          />
          <span className="text-sm text-muted-foreground">
            {t("geonames_sending", {
              sent: number(step.sent),
              total: number(step.total),
            })}
          </span>
        </div>
      )}
      {step.kind === "reading_kohesio" && (
        <div className="flex flex-col gap-1">
          <Progress value={Math.round(step.ratio * 100)} />
          <span className="text-sm text-muted-foreground">
            {t("kohesio_reading", {
              operations: number(step.operations),
              kept: number(step.kept),
            })}
          </span>
        </div>
      )}
      {step.kind === "sending_kohesio" && (
        <div className="flex flex-col gap-1">
          <Progress
            value={Math.round((step.sent / Math.max(step.total, 1)) * 100)}
          />
          <span className="text-sm text-muted-foreground">
            {t("kohesio_sending", {
              sent: number(step.sent),
              total: number(step.total),
            })}
          </span>
        </div>
      )}
      {step.kind === "reading_jpeia" && (
        <div className="flex flex-col gap-1">
          <Progress
            value={
              step.details === undefined
                ? 0
                : Math.round((step.details / Math.max(step.kept, 1)) * 100)
            }
          />
          <span className="text-sm text-muted-foreground">
            {step.details === undefined
              ? t("jpeia_reading_list", {
                  read: number(step.read),
                  kept: number(step.kept),
                })
              : t("jpeia_reading_details", {
                  details: number(step.details),
                  kept: number(step.kept),
                })}
          </span>
        </div>
      )}
      {step.kind === "sending_jpeia" && (
        <div className="flex flex-col gap-1">
          <Progress
            value={Math.round((step.sent / Math.max(step.total, 1)) * 100)}
          />
          <span className="text-sm text-muted-foreground">
            {t("jpeia_sending", {
              sent: number(step.sent),
              total: number(step.total),
            })}
          </span>
        </div>
      )}
      {step.kind === "sent" && (
        <p className="text-sm">
          {t(
            step.source === "geonames"
              ? "geonames_sent"
              : step.source === "kohesio"
                ? "kohesio_sent"
                : step.source === "jpeia"
                  ? "jpeia_sent"
                  : "sent",
            {
              total: number(step.total),
              period: step.period,
              batch: step.batch,
            },
          )}
        </p>
      )}
      {step.kind === "error" && (
        <p className="text-sm text-destructive">
          {t("failed", { error: step.message })}
        </p>
      )}
    </>
  );

  return (
    <div className="mt-2 pb-4 flex flex-col gap-4 max-w-4xl">
      <div>
        <h2 className="text-xl font-semibold">{t("title")}</h2>
        <p className="text-sm text-muted-foreground">{t("intro")}</p>
      </div>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <h3 className="font-medium">{t("isvz_title")}</h3>
          <ol className="list-decimal pl-5 text-sm flex flex-col gap-2">
            <li>
              {t("step_download")}{" "}
              <a
                href={ISVZ_OPEN_DATA}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 underline"
              >
                {t("open_data")}
                <ExternalLink className="size-3" />
              </a>
            </li>
            <li>{t("step_choose")}</li>
            <li>{t("step_night")}</li>
          </ol>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={input}
              type="file"
              accept=".zip,application/zip"
              className="hidden"
              onChange={(event) => choose(event.target.files?.[0])}
            />
            <Button disabled={busy} onClick={() => input.current?.click()}>
              <FileUp />
              {t("choose")}
            </Button>
            <span className="text-xs text-muted-foreground">
              {t("privacy")}
            </span>
          </div>
          {active === "isvz" && status}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <h3 className="font-medium">{t("kohesio_title")}</h3>
          <ol className="list-decimal pl-5 text-sm flex flex-col gap-2">
            <li>
              {t("kohesio_step_download")}{" "}
              <a
                href={KOHESIO_DATA}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 underline"
              >
                {t("kohesio_open")}
                <ExternalLink className="size-3" />
              </a>
            </li>
            <li>{t("kohesio_step_choose")}</li>
            <li>{t("kohesio_step_night")}</li>
          </ol>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={kohesioInput}
              type="file"
              multiple
              accept=".csv,text/csv"
              className="hidden"
              onChange={(event) => chooseKohesio(event.target.files)}
            />
            <Button
              disabled={busy}
              variant="outline"
              onClick={() => kohesioInput.current?.click()}
            >
              <FileUp />
              {t("kohesio_choose")}
            </Button>
            <span className="text-xs text-muted-foreground">
              {t("kohesio_license")}
            </span>
          </div>
          {active === "kohesio" && status}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <h3 className="font-medium">{t("jpeia_title")}</h3>
          <ol className="list-decimal pl-5 text-sm flex flex-col gap-2">
            <li>
              {t("jpeia_step_read")}{" "}
              <a
                href={JPEIA_SITE}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 underline"
              >
                {t("jpeia_open")}
                <ExternalLink className="size-3" />
              </a>
            </li>
            <li>{t("jpeia_step_night")}</li>
          </ol>
          <div className="flex flex-wrap items-center gap-3">
            <Button disabled={busy} variant="outline" onClick={readJapan}>
              <Download />
              {t("jpeia_choose")}
            </Button>
            <span className="text-xs text-muted-foreground">
              {t("jpeia_license")}
            </span>
          </div>
          {active === "jpeia" && status}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <h3 className="font-medium">{t("geonames_title")}</h3>
          <ol className="list-decimal pl-5 text-sm flex flex-col gap-2">
            <li>
              {t("geonames_step_download")}{" "}
              <a
                href={GEONAMES_DUMP}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 underline"
              >
                {t("geonames_open")}
                <ExternalLink className="size-3" />
              </a>
            </li>
            <li>{t("geonames_step_choose")}</li>
            <li>{t("geonames_step_night")}</li>
          </ol>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={citiesInput}
              type="file"
              accept=".zip,application/zip"
              className="hidden"
              onChange={(event) => chooseCities(event.target.files?.[0])}
            />
            <Button
              disabled={busy}
              variant="outline"
              onClick={() => citiesInput.current?.click()}
            >
              <FileUp />
              {t("geonames_choose")}
            </Button>
            <span className="text-xs text-muted-foreground">
              {t("geonames_license")}
            </span>
          </div>
          {active === "geonames" && status}
        </CardContent>
      </Card>

      <MailGroupProposals />

      <div className="flex flex-col gap-2">
        <h3 className="font-medium">{t("history")}</h3>
        {history.error ? (
          <p className="text-sm text-destructive">{history.error.message}</p>
        ) : history.isPending ? null : !history.data?.length ? (
          <p className="text-sm text-muted-foreground">{t("empty")}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("period")}</TableHead>
                <TableHead>{t("deposited")}</TableHead>
                <TableHead className="text-right">{t("records")}</TableHead>
                <TableHead>{t("status")}</TableHead>
                <TableHead>{t("outcome")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.data.map((line) => (
                <TableRow key={line.id}>
                  <TableCell className="whitespace-nowrap">
                    {t(`sources.${line.source_id}`)} · {line.period}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {new Date(line.created_at).toLocaleString(locale, {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {number(line.record_count)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        line.status === "error"
                          ? "destructive"
                          : line.status === "done"
                            ? "default"
                            : "secondary"
                      }
                    >
                      {t(`statuses.${line.status}`)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-normal">
                    {line.status === "done" &&
                    line.result &&
                    line.source_id === "geonames"
                      ? t("geonames_result", {
                          places: number(line.result.places ?? 0),
                          placed: number(line.result.placed ?? 0),
                          projects: number(line.result.projects ?? 0),
                        })
                      : line.status === "done" &&
                          line.result &&
                          (line.source_id === "kohesio" ||
                            line.source_id === "jpeia" ||
                            line.source_id === "mirt" ||
                            line.source_id === "vib" ||
                            line.source_id === "rahmenplan" ||
                            line.source_id === "kpk" ||
                            line.source_id === "ntp" ||
                            line.source_id === "budjetti" ||
                            line.source_id === "standbericht" ||
                            line.source_id === "sfdi")
                        ? t(`${line.source_id}_result`, {
                            kept: number(line.result.kept ?? 0),
                            records: number(line.result.records ?? 0),
                          })
                        : line.status === "done" && line.result
                          ? t("result", {
                              kept: number(line.result.kept ?? 0),
                              stale: number(line.result.stale ?? 0),
                            })
                          : (line.error ?? "")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

SourceImportsPage.path = "/source_imports";
